import { AG_GRID_LOCALE_EN, AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GridComponent } from '@app/components';
import { numericOnlyAllowableKeys } from '@app/constants/numeric-only-allowable-keys';
import { FeatureFlag } from '@app/enums';
import { AgGridHeaderGroup } from '@app/interfaces/ag-grid.interface';
import { Office } from '@app/models/company/office.model';
import { Employee } from '@app/models/employee/employee.model';
import { Schedule } from '@app/models/payroll/schedule.model';
import {
    AdjustableEditItem,
    AdjustableItemPayload,
    AdjustableItemPayloadParams,
    TaxAdjusterTable,
    TaxAdjusterTableEmployeeRow,
    TaxAdjusterTableEmployeeRowLineItem,
    TaxAdjusterTableFormattedRowData,
} from '@app/modules/payroll/interfaces/tax-adjuster.interface';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { TaxAdjusterService } from '@app/modules/payroll/services';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Jurisdiction } from '@app/types/province';
import { TranslateService } from '@ngx-translate/core';
import { CellValueChangedEvent, ColDef, GridOptions } from 'ag-grid-community';
import { flatten, uniq } from 'lodash-es';
import { from } from 'rxjs';

type PayrollId = string;
type AdjustmentPayrollEntry = { payrollId: PayrollId; jurisdiction: string; table?: TaxAdjusterTable };

@Component({
    templateUrl: './tax-adjuster.template.html',
    styleUrls: ['./tax-adjuster.style.scss'],
})
export class TaxAdjusterView implements OnInit {
    @ViewChild('taxAdjusterGrid', { static: false }) taxAdjusterGrid: GridComponent;

    isLoading = true;
    hasEmployeesOnPayroll = false;
    schedule: Schedule;
    currentTaxSeason = new Date().getFullYear() - 1;
    taxSeasonSelected: number;
    enableAdjusterForCurrentYear = false;
    taxAdjustableYears: number[] = [];
    shouldTaxAdjusterViewBeDisplayed = false;
    taxAdjusterTableColumnDefs: Record<PayrollId, ColDef[]> = {};
    taxAdjusterTableRowData: Record<PayrollId, TaxAdjusterTableFormattedRowData[]> = {};
    renderTaxAdjusterTable = false;
    allChangesSaved = true;
    displayErrorState = false;
    errorString = '';
    yearEndAdjustmentPayrolls: AdjustmentPayrollEntry[] = [];
    selectedPayroll?: PayrollId;
    gridOptions: GridOptions;
    employees?: Employee[];
    hasAddFormFlag = toSignal(from(this.featureService.has('tax-adjuster-add-form')));
    addTaxFormDialog: { isOpen: boolean; isLoading: boolean; employeeId?: string; jurisdiction?: Jurisdiction } = {
        isOpen: false,
        isLoading: false,
        employeeId: undefined,
        jurisdiction: undefined,
    };
    officeJurisdictions: Jurisdiction[] = [];

    constructor(
        private auth: AuthService,
        private http: HttpClient,
        private featureService: FeatureService,
        private notify: NotifyService,
        private taxAdjusterService: TaxAdjusterService,
        private translateService: TranslateService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        this.gridOptions = {
            onCellValueChanged: (
                event: CellValueChangedEvent & {
                    data: TaxAdjusterTableFormattedRowData;
                    colDef: { colId: string };
                }
            ): void => {
                this.handleCellEdit(event);
            },
            // Only take the first cell from the clipboard selection, effectively disabling multi-cell paste
            processDataFromClipboard: ({ data }): string[][] => [[data[0][0]]],
            localeText:
                this.auth.employee.language === 'fr_CA'
                    ? {
                          ...AG_GRID_LOCALE_FR,
                          // Mismatched key in our current version of AG-Grid. Fixed in v32 and on
                          // @see https://github.com/ag-grid/ag-grid/commit/c598116378a8a6b963443278e44a7583af9582b5
                          autosizeThiscolumn: AG_GRID_LOCALE_FR.autosizeThisColumn,
                      }
                    : AG_GRID_LOCALE_EN,
        };
    }

    async ngOnInit(): Promise<void> {
        try {
            this.hasEmployeesOnPayroll = await this.determineIfThereAreEmployeesOnPayroll();
            if (this.hasEmployeesOnPayroll) {
                await this.loadFeatureFlags();
                this.schedule = await this.loadSchedule();
                const currentYear = new Date().getFullYear();
                this.taxAdjustableYears = this.getTaxAdjustableYears(currentYear, this.schedule?.allPeriods);
                await this.getYearEndAdjustmentPayrolls(this.currentTaxSeason);
                this.selectedPayroll = this.yearEndAdjustmentPayrolls[0].payrollId;
                this.shouldTaxAdjusterViewBeDisplayed = this.determineIfTaxAdjusterViewShouldBeDisplayed(
                    this.taxAdjustableYears
                );

                if (this.shouldTaxAdjusterViewBeDisplayed) {
                    await this.fetchAdjustmentPayroll(this.selectedPayroll);
                }
            }
        } catch (error) {
            this.notify.error(error);
        }
        this.isLoading = false;
    }

    get shouldShowTabs(): boolean {
        // We only started splitting adjustment payrolls by province in the 2024 tax season
        return this.currentTaxSeason >= 2024;
    }

    get currentTable(): TaxAdjusterTable | undefined {
        return this.yearEndAdjustmentPayrolls.find((pr) => pr.payrollId === this.selectedPayroll)?.table;
    }

    async onTabChange(payrollId: PayrollId): Promise<void> {
        this.isLoading = true;
        try {
            // Fetch table before switching the grid
            await this.fetchAdjustmentPayroll(payrollId);
            this.selectedPayroll = payrollId;
        } catch (e) {
            this.notify.error(e);
        }
        this.isLoading = false;
    }

    getTaxAdjustableYears(currentYear: number, schedulePeriods: any): number[] {
        // Backend currently cannot handle anything before 2021, therefore, the year is hardcoded
        const taxAdjustableYears = schedulePeriods
            .filter((period: any) => period.year !== currentYear && period.year >= 2021)
            .map((period: any) => period.year);
        return taxAdjustableYears;
    }

    buildTableDataset(payrollId: PayrollId, tableData: TaxAdjusterTable): void {
        this.taxAdjusterTableColumnDefs[payrollId] = this.buildTableHeaders(tableData);
        this.taxAdjusterTableRowData[payrollId] = this.buildRowData(tableData);
        this.renderTaxAdjusterTable = true;
    }

    findCellEditableValue(data: TaxAdjusterTableEmployeeRow, colDef: ColDef, tableData: TaxAdjusterTable): boolean {
        const employeeId = data.employee.id;
        const payRawFieldName = colDef.colId;
        const tableEmployeeRow = tableData.employee_rows.find((row) => row.employee.id === employeeId);
        const tableEmployeeLineItems: TaxAdjusterTableEmployeeRowLineItem[] = flatten(
            Object.values(tableEmployeeRow?.line_items ?? [])
        );
        const tableLineItem = tableEmployeeLineItems.find((lineItem) => {
            return lineItem.key === payRawFieldName;
        });
        return tableLineItem?.editable ?? false;
    }

    sanitizeCellAmount(amountValue: any): string {
        const isNonNumeric = isNaN(parseFloat(amountValue));
        const sanitizedValue = parseFloat(amountValue === '' || isNonNumeric ? 0 : amountValue).toFixed(2);
        return sanitizedValue;
    }

    async onYearChangeSelected(): Promise<void> {
        this.renderTaxAdjusterTable = false;
        this.isLoading = true;
        try {
            await this.getYearEndAdjustmentPayrolls(this.taxSeasonSelected);
            this.selectedPayroll = this.yearEndAdjustmentPayrolls[0].payrollId;
            await this.fetchAdjustmentPayroll(this.selectedPayroll);
        } catch (e) {
            this.notify.error(e);
        }
        this.isLoading = false;
    }

    buildTableHeaders(tableData: TaxAdjusterTable): ColDef[] {
        const employeeRow: ColDef[] = [
            {
                field: 'employee.name',
                headerName: this.translateService.instant('payroll.taxAdjuster.employee'),
                pinned: 'left',
                sortable: true,
                sort: 'asc',
            },
        ];
        const headers: [AgGridHeaderGroup[]] = tableData.headers;
        const headerItems: AgGridHeaderGroup[] = flatten(Object.values(headers).map((headerTypeArr) => headerTypeArr));
        const formattedTableHeaders = headerItems.map((item: AgGridHeaderGroup) =>
            this.buildTableHeader(item, tableData)
        );
        return [...employeeRow, ...formattedTableHeaders];
    }

    buildTableHeader(item: AgGridHeaderGroup, table: TaxAdjusterTable): ColDef {
        const column: ColDef = {
            // Define how to get and set the value from the table data.
            // Normally this wouldn't be necessary, as ag-grid handles dot-nested
            // fields, but we sometimes have line items with `.` in their names
            valueGetter: (params) => params.data.amountItems[item.field]?.amount,
            valueSetter: (params) => {
                if (params.newValue === params.data.amountItems[item.field].amount) {
                    return false;
                }

                params.data.amountItems[item.field].amount = params.newValue;

                return true;
            },
            headerName: item.header_name,
            cellClass: !table.table_editable // todo: grab the correct table
                ? 'disabled-grid-cell'
                : ({ data, colDef }): string => {
                      const tableLineItemEditable = this.findCellEditableValue(data, colDef, table);
                      return tableLineItemEditable ? '' : 'disabled-grid-cell';
                  },
            editable: (params): boolean => {
                if (!table.table_editable) {
                    // todo: grab the correct table
                    return false;
                }
                const tableLineItemEditable = this.findCellEditableValue(params.data, params.column.getColDef(), table);
                return tableLineItemEditable;
            },
            colId: item.field,
            cellRenderer: (params): string => {
                const sanitizedValue = this.sanitizeCellAmount(params.value);
                return `$ ${parseFloat(sanitizedValue).toFixed(2)}`;
            },
            suppressKeyboardEvent: (params): boolean => {
                const keyCode = params.event.keyCode;
                const gridShouldSuppressKeyboardEvent =
                    params.editing && numericOnlyAllowableKeys.indexOf(keyCode) === -1;
                if (gridShouldSuppressKeyboardEvent) {
                    params.event.returnValue = false;
                }
                return gridShouldSuppressKeyboardEvent;
            },
        };

        if (item.field === 'jurisdiction') {
            const formatter = (params): string => {
                return params.value;
            };
            column.cellDataType = 'text';
            column.cellRenderer = formatter;
            column.valueFormatter = formatter;
            column.filter = 'agTextColumnFilter';
        }

        return column;
    }

    buildRowData(tableData: TaxAdjusterTable): TaxAdjusterTableFormattedRowData[] {
        const rowData = tableData.employee_rows;
        const formattedRowData = rowData.map((row) => {
            const lineItems = flatten(Object.values(row.line_items)).map((lineItem: any) => {
                return {
                    [lineItem.key]: {
                        amount: lineItem.amount,
                        id: lineItem.id,
                        type: lineItem.type,
                        name: lineItem.name,
                        editable: lineItem.editable,
                        ...(lineItem.statutory && { statutory: lineItem.statutory }),
                        ...(lineItem.payee && { payee: lineItem.payee }),
                        ...(lineItem.employee_benefit_id && { employee_benefit_id: lineItem.employee_benefit_id }),
                        ...(lineItem.employee_deduction_id && {
                            employee_deduction_id: lineItem.employee_deduction_id,
                        }),
                        ...(lineItem.display_name && { display_name: lineItem.display_name }),
                    },
                };
            });
            const amountItems = Object.assign({}, ...lineItems);
            return {
                employee: row.employee,
                amountItems,
                paystubId: row.paystub_id,
            };
        });
        return formattedRowData;
    }

    determineIfTaxAdjusterViewShouldBeDisplayed(taxAdjustableYears: number[]): boolean {
        if (!taxAdjustableYears.length) {
            return false;
        }
        const isCurrentTaxSeasonInPayPeriods: boolean = taxAdjustableYears.indexOf(this.currentTaxSeason) > -1;
        if (isCurrentTaxSeasonInPayPeriods && !this.enableAdjusterForCurrentYear) {
            if (taxAdjustableYears.length === 1) {
                return false;
            }
            this.removeCurrentTaxSeasonFromSelectableTaxSeasons();
            return true;
        }
        this.taxSeasonSelected = this.currentTaxSeason;
        return true;
    }

    removeCurrentTaxSeasonFromSelectableTaxSeasons(): void {
        const indexOfCurrentTaxSeason = this.taxAdjustableYears.indexOf(this.currentTaxSeason);
        this.taxAdjustableYears.splice(indexOfCurrentTaxSeason, 1);
        this.taxSeasonSelected = this.taxAdjustableYears[this.taxAdjustableYears.length - 1];
    }

    buildPayload(itemPaystubId: number, itemToEdit: AdjustableEditItem): AdjustableItemPayload {
        const payload = {
            data: {
                attributes: {
                    paystubId: itemPaystubId,
                    name: itemToEdit.name,
                    amount: this.sanitizeCellAmount(itemToEdit.amount),
                    ...(itemToEdit.statutory && { statutory: itemToEdit.statutory }),
                    ...(itemToEdit.payee && { payee: itemToEdit.payee }),
                    ...(itemToEdit.employee_benefit_id && { employee_benefit_id: itemToEdit.employee_benefit_id }),
                    ...(itemToEdit.employee_deduction_id && {
                        employee_deduction_id: itemToEdit.employee_deduction_id,
                    }),
                    ...(itemToEdit.display_name && { display_name: itemToEdit.display_name }),
                },
            },
        };
        return payload;
    }

    assignEditedItemValue(
        taxAdjusterSaveResponse: AdjustableItemPayloadParams,
        itemToEdit: any,
        itemToEditId: number | null
    ): void {
        if (!itemToEditId) {
            itemToEdit.id = taxAdjusterSaveResponse.id;
            itemToEdit.amount = taxAdjusterSaveResponse.amount;
        }
    }

    async handleCellEdit(
        cellEditEvent: CellValueChangedEvent & {
            data: TaxAdjusterTableFormattedRowData;
            colDef: { colId: string };
        }
    ): Promise<void> {
        if (!this.selectedPayroll) {
            return;
        }
        if (cellEditEvent.newValue === cellEditEvent.oldValue) {
            return;
        }
        this.displayErrorState = false;
        this.errorString = '';
        this.allChangesSaved = !this.allChangesSaved;
        const itemToEdit = cellEditEvent.data.amountItems[cellEditEvent.colDef.colId];
        const itemPaystubId = cellEditEvent.data.paystubId;
        const employee = cellEditEvent.data.employee;
        const payload = this.buildPayload(itemPaystubId, itemToEdit);
        const itemEditId = itemToEdit.id !== null ? itemToEdit.id : null;
        try {
            let taxAdjusterSaveResponse: AdjustableItemPayloadParams;
            switch (itemToEdit.type) {
                case 'payable_items':
                    taxAdjusterSaveResponse = await this.taxAdjusterService.savePayableItem(payload, itemEditId);
                    this.assignEditedItemValue(taxAdjusterSaveResponse, itemToEdit, itemEditId);
                    break;
                case 'deduction_items':
                    taxAdjusterSaveResponse = await this.taxAdjusterService.saveDeductionItem(payload, itemEditId);
                    this.assignEditedItemValue(taxAdjusterSaveResponse, itemToEdit, itemEditId);
                    break;
                case 'additional_pay_items':
                    taxAdjusterSaveResponse = await this.taxAdjusterService.saveAdditionalPayItem(payload, itemEditId);
                    this.assignEditedItemValue(taxAdjusterSaveResponse, itemToEdit, itemEditId);
                    break;
                case 'benefit_deduction':
                case 'benefit_deduction_items':
                    taxAdjusterSaveResponse = await this.taxAdjusterService.saveEmployeeBenefitDeductionItem(
                        payload,
                        employee.hr_employee_id,
                        payload.data.attributes.employee_benefit_id,
                        itemEditId
                    );
                    this.assignEditedItemValue(taxAdjusterSaveResponse, itemToEdit, itemEditId);
                    break;
                case 'employee_deduction':
                case 'employee_deduction_items':
                    taxAdjusterSaveResponse = await this.taxAdjusterService.saveEmployeeDeductionDeductionItem(
                        payload,
                        employee.hr_employee_id,
                        payload.data.attributes.employee_deduction_id,
                        itemEditId
                    );
                    this.assignEditedItemValue(taxAdjusterSaveResponse, itemToEdit, itemEditId);
                    break;
                case 'benefit_item':
                    taxAdjusterSaveResponse = await this.taxAdjusterService.saveCompanyContributionBenefitItem(
                        payload,
                        employee.hr_employee_id,
                        payload.data.attributes.employee_benefit_id,
                        itemEditId
                    );
                    this.assignEditedItemValue(taxAdjusterSaveResponse, itemToEdit, itemEditId);
                    break;
            }
        } catch (error) {
            await this.handleErrorState(error, employee, cellEditEvent);
        }
        this.allChangesSaved = !this.allChangesSaved;
        this.taxAdjusterTableColumnDefs[this.selectedPayroll] = this.buildTableHeaders(this.currentTable);
    }

    async handleErrorState(error: HttpErrorResponse, employee, cellEditEvent): Promise<void> {
        if (!this.currentTable) return;
        const tableLocked = error.status === 423 || error.statusText === 'Locked';
        this.currentTable.table_editable = tableLocked
            ? await this.fetchTaxAdjusterTableEditableValue(this.taxSeasonSelected)
            : this.currentTable.table_editable;
        this.errorString = tableLocked
            ? this.translateService.instant('payroll.taxAdjuster.cannotEdit', {
                  employeeName: employee.name,
                  columnHeaderName: cellEditEvent.colDef?.headerName,
              })
            : this.translateService.instant('payroll.taxAdjuster.cannotSave', {
                  employeeName: employee.name,
                  columnHeaderName: cellEditEvent.colDef?.headerName,
              });
        this.displayErrorState = true;
        this.taxAdjusterGrid.undoCellEdit();
        this.notify.error(this.errorString);
    }

    handleFilter(query: string, tableData?: TaxAdjusterTable): void {
        if (!tableData) return;
        if (!this.selectedPayroll) return;
        const employeeRowData: TaxAdjusterTableFormattedRowData[] = this.buildRowData(tableData);
        const formattedQuery: string = query === null ? '' : query.toLowerCase();
        const filteredEmployees: TaxAdjusterTableFormattedRowData[] = employeeRowData.filter(
            (row) => row.employee.name.toLowerCase().indexOf(formattedQuery) > -1
        );
        this.taxAdjusterTableRowData[this.selectedPayroll] = filteredEmployees;
    }

    closeAddTaxFormDialog(): void {
        this.addTaxFormDialog = { isOpen: false, isLoading: false, jurisdiction: undefined, employeeId: undefined };
    }

    /**
     * Fetches all provinces that the company has offices in. Used for the add tax form dialog
     */
    async loadOfficeJurisdictions(): Promise<void> {
        // If we've already loaded the offices, there's no need to re-fetch the provinces.
        if (this.officeJurisdictions.length) {
            return;
        }
        this.addTaxFormDialog.isLoading = true;
        this.officeJurisdictions = await Office.param('company', this.auth.company.id)
            .all()
            .then(([offices]) =>
                uniq(
                    offices
                        .filter((office: Office) => office.country === 'Canada')
                        .map((office: Office) => office.province as Jurisdiction)
                )
            );
        this.addTaxFormDialog.isLoading = false;
    }

    async handleAddTaxForm(): Promise<void> {
        const { jurisdiction, employeeId } = this.addTaxFormDialog;

        if (!jurisdiction || !employeeId) {
            return;
        }

        this.addTaxFormDialog.isLoading = true;
        try {
            await this.taxAdjusterService.addTaxFormToYearEndAdjuster({
                employeeId,
                jurisdiction,
                year: this.taxSeasonSelected,
            });
            this.closeAddTaxFormDialog();
            this.notify.success({
                key: 'payroll.taxAdjuster.addTaxForm.success',
                params: { year: this.taxSeasonSelected },
            });
            // If the newly added form belongs to a jurisdiction that previously had no payrolls, we need to re-load the collection of payrolls from the BE to populate the tabs
            if (!this.yearEndAdjustmentPayrolls.find((payroll) => payroll.jurisdiction === jurisdiction)) {
                await this.getYearEndAdjustmentPayrolls(this.taxSeasonSelected);
            }
            // Reload the table that the form was added to
            const selectedPayroll = this.yearEndAdjustmentPayrolls.find(
                (payroll) => payroll.jurisdiction === jurisdiction
            );
            if (selectedPayroll) {
                this.isLoading = true;
                await this.fetchAdjustmentPayroll(selectedPayroll.payrollId, { forceReload: true });
            }
        } catch {
            this.notify.error('payroll.taxAdjuster.addTaxForm.cannotAdd');
        }
        this.addTaxFormDialog.isLoading = false;
        this.isLoading = false;
    }

    async fetchTaxAdjusterTableEditableValue(taxSeasonSelected: number): Promise<boolean> {
        const taxAdjusterTableEditable = await this.taxAdjusterService.fetchTaxAdjusterTableEditable(
            taxSeasonSelected,
            true
        );
        return taxAdjusterTableEditable;
    }

    private async determineIfThereAreEmployeesOnPayroll(): Promise<boolean> {
        this.employees = await Employee.param('company', this.auth.company.id)
            .where('onPayroll', 1)
            .orderBy('lastName', 'ASC')
            .where('status', ['active', 'terminated', 'onLeave'])
            .limit(0)
            .get()
            .then(([employees]) => employees);
        return !!this.employees?.length;
    }

    private async loadFeatureFlags(): Promise<void> {
        this.enableAdjusterForCurrentYear = await this.featureService.has(FeatureFlag.enableAdjusterForCurrentYear);
    }

    private async loadSchedule(): Promise<Schedule> {
        try {
            const res = await this.http
                .get(PayrollResources.Schedule.replace(':company', this.auth.company.id.toString()))
                .toPromise();
            return new Schedule(res['data']['attributes']);
        } catch (err) {
            this.notify.error(err.message || 'payroll.taxAdjuster.couldNotLoadSchedule');
            this.isLoading = false;
        }
    }

    private async getYearEndAdjustmentPayrolls(taxSeason: number): Promise<void> {
        let params = new HttpParams();
        params = params.append('year', taxSeason);
        const url = PayrollResources.YearEndAdjustmentPayrolls;
        const yearEndAdjustmentPayrolls = await this.http
            .get<{ data: AdjustmentPayrollEntry[] }>(url, { params: params })
            .toPromise();
        this.yearEndAdjustmentPayrolls = yearEndAdjustmentPayrolls['data'];
    }

    /**
     * Fetch the tables for each adjustment payroll currently loaded,
     * and build their datasets
     */
    private async fetchAdjustmentPayroll(payrollId: PayrollId, options?: { forceReload?: boolean }): Promise<void> {
        const payroll = this.yearEndAdjustmentPayrolls.find((p) => p.payrollId == payrollId);
        if (payroll === undefined) return;
        if (payroll.table === undefined || options?.forceReload) {
            const result = await this.taxAdjusterService.fetchYearEndAdjustmentPayrollTable(payrollId);
            payroll.table = result;
            this.buildTableDataset(payroll.payrollId, result);
        }
    }
}
